import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { enrichRunWithTelemetryData } from '../utils/enrichRunWithTelemetryData';
import { computeRoughnessRaw } from '../utils/roughnessScore';
import { buildSuspensionSeries } from '../utils/suspensionAnalysis';
import {
  fetchRoughnessCalibration,
  saveRoughnessCalibration,
  newBenchmarkId,
} from '../utils/roughnessCalibration';

function runLabel(run) {
  return (
    run?.name ||
    run?.title ||
    run?.runName ||
    run?.strava?.name ||
    `Run ${String(run?.id || '').slice(0, 8)}`
  );
}

export default function AdminRoughnessCalibration() {
  const { user } = useAuth();
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [benchmarks, setBenchmarks] = useState([]);
  const [calLoading, setCalLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [selectedRunId, setSelectedRunId] = useState('');
  const [assignedScore, setAssignedScore] = useState(50);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCalLoading(true);
      try {
        const cal = await fetchRoughnessCalibration();
        if (!cancelled) setBenchmarks(cal.benchmarks);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load calibration');
      } finally {
        if (!cancelled) setCalLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRunsLoading(true);
      try {
        const snap = await getDocs(collection(db, 'runs'));
        if (cancelled) return;
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const ta = a.createdAt?.toMillis?.() ?? a.createdAt?.seconds * 1000 ?? 0;
            const tb = b.createdAt?.toMillis?.() ?? b.createdAt?.seconds * 1000 ?? 0;
            return tb - ta;
          })
          .slice(0, 400);
        setRuns(list);
        if (list.length && !selectedRunId) setSelectedRunId(list[0].id);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load runs');
      } finally {
        if (!cancelled) setRunsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedRunId]);

  const runOptions = useMemo(
    () =>
      runs.map((r) => ({
        id: r.id,
        label: `${runLabel(r)} (${r.id.slice(0, 8)}…)`,
      })),
    [runs],
  );

  const persistBenchmarks = useCallback(async (next) => {
    setSaving(true);
    setError('');
    try {
      await saveRoughnessCalibration({ benchmarks: next });
      setBenchmarks(next);
      setStatus('Calibration saved (applies to all riders).');
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, []);

  const handleAddBenchmark = async () => {
    if (!selectedRunId || !user) return;
    setAdding(true);
    setError('');
    setStatus('');
    try {
      const runDoc = runs.find((r) => r.id === selectedRunId);
      if (!runDoc) throw new Error('Run not found');
      const enriched = await enrichRunWithTelemetryData(selectedRunId, { ...runDoc, id: selectedRunId }, { user });
      const td = enriched.telemetryData;
      if (!Array.isArray(td) || td.length < 10) {
        throw new Error('Run has no usable suspension telemetry');
      }
      const series = buildSuspensionSeries(td);
      const rawResult = computeRoughnessRaw(series);
      if (!rawResult) throw new Error('Could not compute roughness raw for this run');

      const score = Math.max(0, Math.min(200, Number(assignedScore)));
      const entry = {
        id: newBenchmarkId(),
        runId: selectedRunId,
        runLabel: runLabel(enriched),
        assignedScore: score,
        raw: rawResult.raw,
      };

      const exists = benchmarks.some((b) => b.runId === selectedRunId);
      const next = exists
        ? benchmarks.map((b) => (b.runId === selectedRunId ? { ...b, ...entry, id: b.id } : b))
        : [...benchmarks, entry];

      await persistBenchmarks(next);
    } catch (e) {
      setError(e?.message || 'Add benchmark failed');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (id) => {
    const next = benchmarks.filter((b) => b.id !== id);
    await persistBenchmarks(next);
  };

  return (
    <section className="admin-page-section admin-roughness-cal">
      <h2>Roughness calibration</h2>
      <p className="admin-roughness-cal-hint">
        Tag reference runs with a target roughness score (0 = calm, 50 = typical trail, 100 = quite rough).
        The app maps every ride&apos;s raw harshness to 0–200 using these points. Needs at least two benchmarks
        for a custom curve; otherwise built-in defaults apply.
      </p>

      {calLoading ? <p>Loading calibration…</p> : null}
      {runsLoading ? <p>Loading runs for dropdown…</p> : null}
      {error ? (
        <p className="admin-roughness-cal-error" role="alert">
          {error}
        </p>
      ) : null}
      {status ? <p className="admin-roughness-cal-status">{status}</p> : null}

      <div className="admin-roughness-cal-form">
        <label>
          Reference run
          <select
            value={selectedRunId}
            onChange={(e) => setSelectedRunId(e.target.value)}
            disabled={runsLoading || adding || saving}
          >
            {runOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Assigned score (0–200)
          <input
            type="number"
            min={0}
            max={200}
            step={1}
            value={assignedScore}
            onChange={(e) => setAssignedScore(Number(e.target.value))}
            disabled={adding || saving}
          />
        </label>
        <button type="button" onClick={handleAddBenchmark} disabled={adding || saving || !selectedRunId}>
          {adding ? 'Computing…' : 'Add / update benchmark'}
        </button>
      </div>

      {benchmarks.length < 2 ? (
        <p className="admin-roughness-cal-warn">
          Fewer than 2 benchmarks — riders see scores from default anchors (0 / 50 / 100).
        </p>
      ) : null}

      {benchmarks.length > 0 ? (
        <table className="run-table admin-roughness-cal-table">
          <thead>
            <tr>
              <th>Run</th>
              <th>Raw</th>
              <th>Assigned score</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {benchmarks
              .slice()
              .sort((a, b) => a.raw - b.raw)
              .map((b) => (
                <tr key={b.id}>
                  <td>{b.runLabel || b.runId}</td>
                  <td>{Number(b.raw).toFixed(4)}</td>
                  <td>{b.assignedScore}</td>
                  <td>
                    <button type="button" onClick={() => handleRemove(b.id)} disabled={saving}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      ) : (
        <p>No benchmarks yet.</p>
      )}
    </section>
  );
}
